import React, { useState, useEffect } from 'react'
import { CreditCard, FileText, DollarSign, AlertCircle, ExternalLink, CheckCircle, Clock } from 'lucide-react'

interface BillingSettingsProps {
  currentUser?: any
}

const BillingSettings: React.FC<BillingSettingsProps> = ({ currentUser }) => {
  const [billingInfo, setBillingInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [usageStats, setUsageStats] = useState<any>(null)

  useEffect(() => {
    const controller = new AbortController()

    const loadData = async () => {
      try {
        setLoading(true)
        const token = localStorage.getItem('auth-token')
        const headers: HeadersInit = {}
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const [billingResponse, usageResponse] = await Promise.all([
          fetch('/api/billing-settings', {
            credentials: 'include',
            headers,
            signal: controller.signal
          }),
          fetch('/api/usage-stats', {
            credentials: 'include',
            headers,
            signal: controller.signal
          })
        ])

        if (billingResponse.ok) {
          const data = await billingResponse.json()
          setBillingInfo(data)
        }

        if (usageResponse.ok) {
          const data = await usageResponse.json()
          setUsageStats(data)
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return // Ignore cancelled requests
        }
        console.error('Failed to load billing data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()

    return () => controller.abort()
  }, [])

  const loadBillingInfo = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/billing-settings', {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setBillingInfo(data)
      }
    } catch (error) {
      console.error('Failed to load billing info:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadUsageStats = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/usage-stats', {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setUsageStats(data)
      }
    } catch (error) {
      console.error('Failed to load usage stats:', error)
    }
  }

  const handleSetupBilling = async (method: 'stripe' | 'invoice') => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/billing-settings', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ method })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.setupUrl) {
          // Redirect to Stripe payment link setup or invoice portal
          window.location.href = data.setupUrl
        } else {
          alert('Billing setup initiated. Check your email for next steps.')
          loadBillingInfo()
        }
      } else {
        alert('Failed to setup billing. Please try again.')
      }
    } catch (error) {
      console.error('Billing setup error:', error)
      alert('Failed to setup billing. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Clock className="h-6 w-6 animate-spin text-primary-600 mr-2" />
          <span className="text-gray-600">Loading billing information...</span>
        </div>
      </div>
    )
  }

  // Calculate if over free tier
  const currentDomainCount = usageStats?.currentMonthDomains || 0
  const rollingAverage = usageStats?.twelveMonthAverage || 0
  const isOverFreeTier = rollingAverage > 25
  const estimatedMonthlyCost = isOverFreeTier ? rollingAverage * 10 : 0

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Billing & Usage</h2>
        <p className="text-gray-600 mt-1">Manage your subscription and view usage statistics</p>
      </div>

      {/* Usage Statistics */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-md font-semibold text-gray-900 mb-4">Current Usage</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">This Month</span>
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-2">{currentDomainCount}</p>
            <p className="text-xs text-gray-500 mt-1">Active domains</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">12-Month Average</span>
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-2">{rollingAverage.toFixed(1)}</p>
            <p className="text-xs text-gray-500 mt-1">Domains (used for billing)</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Estimated Cost</span>
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              ${estimatedMonthlyCost.toFixed(0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Per month</p>
          </div>
        </div>

        {!isOverFreeTier && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-900">You're on the free tier!</p>
              <p className="text-xs text-green-700 mt-1">
                You have {(25 - rollingAverage).toFixed(1)} domains remaining before billing starts.
              </p>
            </div>
          </div>
        )}

        {isOverFreeTier && !billingInfo?.paymentMethod && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">Action Required: Setup Billing</p>
              <p className="text-xs text-yellow-700 mt-1">
                Your 12-month average ({rollingAverage.toFixed(1)} domains) exceeds the free tier (25 domains).
                Please setup billing below to continue service.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pricing Information */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-md font-semibold text-blue-900 mb-2">Pricing Structure</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li className="flex items-start">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <span><strong>Free tier:</strong> Up to 25 active domains (12-month average)</span>
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <span><strong>Paid tier:</strong> $10 per domain per month (for ALL domains once you exceed 25)</span>
          </li>
          <li className="flex items-start">
            <CheckCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <span><strong>Example:</strong> 100 domains = $1,000/month</span>
          </li>
        </ul>
      </div>

      {/* Payment Method Setup */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-md font-semibold text-gray-900 mb-4">Payment Method</h3>

        {billingInfo?.paymentMethod ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {billingInfo.paymentMethod === 'stripe' ? (
                  <CreditCard className="h-5 w-5 text-green-600 mr-2" />
                ) : (
                  <FileText className="h-5 w-5 text-green-600 mr-2" />
                )}
                <div>
                  <p className="text-sm font-semibold text-green-900">
                    {billingInfo.paymentMethod === 'stripe' ? 'Stripe Payment Link' : 'Invoice Billing'}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    {billingInfo.paymentMethod === 'stripe'
                      ? 'Monthly invoices sent automatically via Stripe'
                      : 'Invoices sent manually to your email'
                    }
                  </p>
                </div>
              </div>
              <a
                href={billingInfo.portalUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-green-600 hover:text-green-700 flex items-center"
              >
                Manage
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Choose your preferred payment method. No credit card details are stored on our servers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Stripe Option */}
              <button
                onClick={() => handleSetupBilling('stripe')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition text-left"
              >
                <div className="flex items-start">
                  <CreditCard className="h-6 w-6 text-primary-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Stripe Payment Link</h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Automated monthly invoices via Stripe. Pay with credit card, ACH, or bank transfer.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>✓ Automatic billing</li>
                      <li>✓ Secure payment portal</li>
                      <li>✓ Multiple payment methods</li>
                    </ul>
                  </div>
                </div>
              </button>

              {/* Invoice Option */}
              <button
                onClick={() => handleSetupBilling('invoice')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition text-left"
              >
                <div className="flex items-start">
                  <FileText className="h-6 w-6 text-primary-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Invoice Billing</h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Manual invoices sent to your email. Net 30 payment terms.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>✓ Net 30 terms</li>
                      <li>✓ Wire transfer / ACH</li>
                      <li>✓ Purchase order support</li>
                    </ul>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice History */}
      {billingInfo?.invoices && billingInfo.invoices.length > 0 && (
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-md font-semibold text-gray-900 mb-4">Invoice History</h3>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {billingInfo.invoices.map((invoice: any) => (
              <div key={invoice.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Invoice #{invoice.number}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(invoice.date).toLocaleDateString()} • {invoice.domains} domains
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-gray-900">
                    ${invoice.amount.toFixed(2)}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    invoice.status === 'paid'
                      ? 'bg-green-100 text-green-800'
                      : invoice.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {invoice.status}
                  </span>
                  {invoice.url && (
                    <a
                      href={invoice.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default BillingSettings
